import type { TOC } from '@ember/component/template-only';
import type { ComponentLike } from '@glint/template';

// ---------------------------------------------------------------------------
// IconBadge – colored icon badge for lists, cards, and section headers
// ---------------------------------------------------------------------------

/**
 * Color theme options for the icon badge
 */
export type IconBadgeColor =
  | 'emerald'
  | 'blue'
  | 'violet'
  | 'amber'
  | 'teal'
  | 'red'
  | 'green'
  | 'indigo'
  | 'pink'
  | 'gray'
  | 'orange';

/**
 * Size options for the icon badge
 */
export type IconBadgeSize = 'sm' | 'md' | 'lg';

interface IconBadgeSignature {
  Args: {
    /** Phosphor icon component to display */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Color theme for background and icon */
    color?: IconBadgeColor;
    /** Size variant */
    size?: IconBadgeSize;
  };
  Element: HTMLDivElement;
}

const colorClasses: Record<IconBadgeColor, { bg: string; text: string }> = {
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
  violet: { bg: 'bg-violet-100', text: 'text-violet-600' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
  teal: { bg: 'bg-teal-100', text: 'text-teal-600' },
  red: { bg: 'bg-red-100', text: 'text-red-600' },
  green: { bg: 'bg-green-100', text: 'text-green-600' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600' },
  pink: { bg: 'bg-pink-100', text: 'text-pink-600' },
  gray: { bg: 'bg-gray-100', text: 'text-gray-600' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-600' },
};

const sizeClasses: Record<IconBadgeSize, { container: string; iconSize: number }> = {
  sm: { container: 'p-1.5 rounded-md', iconSize: 16 },
  md: { container: 'p-2 rounded-lg', iconSize: 20 },
  lg: { container: 'p-3 rounded-xl', iconSize: 24 },
};

const getColorClasses = (color: IconBadgeColor = 'gray') => colorClasses[color];
const getSizeClasses = (size: IconBadgeSize = 'md') => sizeClasses[size];

/**
 * IconBadge - A colored icon badge for lists and cards
 *
 * Small presentational component for displaying a Phosphor icon with a
 * colored background. Used throughout as a building block for navigation
 * cards, section panels, and list items.
 *
 * @example Basic usage
 * ```gts
 * import IconBadge from 'tio-reimagined/components/icon-badge';
 * import { PhTrophy } from 'ember-phosphor-icons';
 *
 * <IconBadge @icon={{PhTrophy}} @color="violet" />
 * ```
 *
 * @example Large size
 * ```gts
 * <IconBadge @icon={{PhGraduationCap}} @color="emerald" @size="lg" />
 * ```
 */
const IconBadge: TOC<IconBadgeSignature> = <template>
  {{#let (getColorClasses @color) (getSizeClasses @size) as |colors sizes|}}
    <div
      data-test-icon-badge
      class="{{sizes.container}} {{colors.bg}} {{colors.text}} flex-shrink-0"
      aria-hidden="true"
      ...attributes
    >
      {{#let @icon as |Icon|}}
        <Icon @size={{sizes.iconSize}} />
      {{/let}}
    </div>
  {{/let}}
</template>;

export default IconBadge;
export type { IconBadgeSignature };
